import {AuthProfile, Wish} from "@/api/types.ts";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {Avatar, Blockquote, Caption, IconButton, Image, Text} from "@telegram-apps/telegram-ui";
import {TbGift, TbPencil} from "react-icons/tb";
import {useTranslation} from "react-i18next";
import {getUserAcronym} from "@/helpers/getUserAcronym.ts";
import {Link} from "@/components/Link/Link.tsx";
import "./styles.css";

export const GiftedWishPreview = (
  {wish, showGiftedBy, onEditClick, onNavigateAway, isGiftedByLinkDisabled = false, readonly = false}:
  {
    wish: Wish,
    showGiftedBy: boolean,
    onEditClick?: () => void,
    onNavigateAway?: () => void,
    isGiftedByLinkDisabled?: boolean,
    readonly?: boolean,
  }
) => {
  const {t} = useTranslation();
  const middleSizeImage = wish.image?.sizes.length && wish.image.sizes.length > 1
    ? wish.image.sizes[1]
    : wish.image?.sizes[0];

  let profileFromContext: AuthProfile | undefined = undefined;
  try {
    const {authProfile} = useAuthProfile();
    profileFromContext = authProfile;
  } catch {
    profileFromContext = undefined;
  }

  const giftedByUser = wish.bookedByUser;
  const santaGame = wish.bookedInSecretSantaGame;
  const isOwner = wish.userId === profileFromContext?.user.id;
  const giftedByRoute = santaGame ? `/main/secret-santa/${santaGame.id}` : `/profiles/${giftedByUser?.id}`;
  const isGiftedByCurrentUser = !santaGame && giftedByUser?.id === profileFromContext?.user.id;
  const isGiftedByLinkReadonly = readonly || isGiftedByLinkDisabled || Boolean(santaGame && !isOwner);
  const giftedByContent = santaGame ? (
    <>
      <Avatar src={`${import.meta.env.BASE_URL}secret-santa.webp`} size={24} />
      <Caption weight="2" className={isGiftedByLinkReadonly ? undefined : "tg-link-color"}>
        {t("secretSanta.messages.secretSanta")}
      </Caption>
    </>
  ) : giftedByUser && (
    <>
      <Avatar
        src={giftedByUser.photo?.sizes[0]?.link}
        acronym={getUserAcronym(giftedByUser.fullname)}
        size={24}
      />
      <Caption weight="2" className={isGiftedByLinkReadonly ? undefined : "tg-link-color"}>
        {giftedByUser.fullname}
      </Caption>
    </>
  );

  return (
    <div className="gifted-wish-preview">
      {onEditClick && !readonly && (
        <IconButton
          size="s"
          mode="plain"
          className="gifted-wish-preview__edit"
          aria-label={t("action.edit")}
          onClick={onEditClick}
        >
          <TbPencil size={18}/>
        </IconButton>
      )}
      <div className="gifted-wish-preview__main">
        <Image
          className="shiny-image"
          src={middleSizeImage?.link}
          style={{background: wish.randomGradient}}
          fallbackIcon={<TbGift color="#fff" size={42}/>}
          // @ts-expect-error size may have this value
          size={72}
        />
        <div className={`gifted-wish-preview__content ${onEditClick && !readonly ? "gifted-wish-preview__content--editable" : ""}`}>
          <Caption weight="2" className="text-color-hint">{t("wish.gifted.previewLabel")}</Caption>
          <Text weight="2" className="gifted-wish-preview__title">{wish.title}</Text>
          {wish.description && <Caption className="gifted-wish-preview__description text-color-hint">{wish.description}</Caption>}
          {onEditClick && readonly && (
            <Caption
              Component="button"
              weight="2"
              className="gifted-wish-preview__description-edit tg-link-color"
              onClick={onEditClick}
            >
              {t("wish.gifted.editDescription")}
            </Caption>
          )}
        </div>
      </div>
      {showGiftedBy && (santaGame || giftedByUser) && (
        <div className="gifted-wish-preview__gifted-by">
          <Caption weight="2" className="text-color-hint">
            {isGiftedByCurrentUser ? t("wish.gifted.giftToSelf") : t("wish.gifted.giftFrom")}
          </Caption>
          {!isGiftedByCurrentUser && isGiftedByLinkReadonly && (
            <div className="flex items-center gap-2">
              {giftedByContent}
            </div>
          )}
          {!isGiftedByCurrentUser && !isGiftedByLinkReadonly && (
            <Link
              to={giftedByRoute}
              className="flex items-center gap-2 no-underline"
              onClick={onNavigateAway}
            >
              {giftedByContent}
            </Link>
          )}
        </div>
      )}
      {wish.giftedComment && (
        <Blockquote type="text" className="gifted-wish-preview__comment">
          {wish.giftedComment}
        </Blockquote>
      )}
    </div>
  );
};
